import React, { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardActionArea,
    CardMedia,
    Chip,
    useTheme,
    IconButton,
    CircularProgress,
    Fade
} from '@mui/material';
import { TypographyVariant } from '@mui/material/styles';
import { ArrowBackIosNew, ArrowForwardIos, Circle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import blackLogo from '../assets/Black logo.png';

// ==============================
// TYPE DEFINITIONS
// ==============================

// Exporting this interface so the Parent component can use it
export interface ArticleData {
    articleId: number;
    articleCategory: string;
    articleTitle: string;
    imageUrl: string;
    position: string;
    publishDate: string;
}

interface ArticleCardProps {
    article: ArticleData;
    aspectRatio?: string;
    titleVariant?: TypographyVariant;
    tagColor?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'default';
}

interface CommercialArticlesSectionProps {
    articles: ArticleData[];
    isLoading?: boolean;
}

// ==============================
// REUSABLE ARTICLE CARD COMPONENT
// ==============================

const ArticleCard: React.FC<ArticleCardProps> = ({
    article,
    aspectRatio = '16 / 9',
    titleVariant = 'h6',
    tagColor = 'primary'
}) => {
    const theme = useTheme();
    const navigate = useNavigate();

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? dateString : date.toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric',
        });
    };

    return (
        <Card
            sx={{
                width: '100%',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: theme.shadows[4],
                aspectRatio: aspectRatio,
            }}
        >
            <CardActionArea
                onClick={() => navigate(`/articles/${article.articleId}`)}
                sx={{ height: '100%', width: '100%' }}
            >
                <CardMedia
                    component="img"
                    image={article.imageUrl}
                    alt={article.articleTitle}
                    sx={{
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease-in-out',
                        '&:hover': { transform: 'scale(1.05)' },
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute', bottom: 0, left: 0, width: '100%',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 100%)',
                        padding: theme.spacing(3),
                        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                        height: '60%',
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                        <Chip
                            label={article.articleCategory}
                            color={tagColor}
                            size="small"
                            sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 }}
                        />
                        <Typography
                            variant={titleVariant}
                            component="h3"
                            sx={{
                                color: 'white', fontWeight: 800, lineHeight: 1.2, mt: 1,
                                display: '-webkit-box', overflow: 'hidden',
                                WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
                            }}
                        >
                            {article.articleTitle}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
                            {formatDate(article.publishDate)}
                        </Typography>
                    </Box>
                </Box>
            </CardActionArea>
        </Card>
    );
};

// ==============================
// MAIN SECTION COMPONENT
// ==============================

const CommercialArticlesSection: React.FC<CommercialArticlesSectionProps> = ({ 
    articles, 
    isLoading = false 
}) => {
    const [activeStep, setActiveStep] = useState(0);

    // Filter logic moved from useEffect to useMemo
    const { heroArticles, gridArticles } = useMemo(() => {
        if (!articles || articles.length === 0) {
            return { heroArticles: [], gridArticles: [] };
        }

        let heroes = articles.filter(a => a.position === 'hero');
        let grid = articles.filter(a => a.position === 'grid');

        // Fallback: If no hero defined, take the first article
        if (heroes.length === 0 && articles.length > 0) {
            heroes.push(articles[0]);
            grid = grid.filter(a => a.articleId !== articles[0].articleId);
        } else {
            // Ensure no duplicates in grid if they are in hero
            const heroIds = heroes.map(h => h.articleId);
            grid = grid.filter(a => !heroIds.includes(a.articleId));
        }

        return { heroArticles: heroes, gridArticles: grid };
    }, [articles]);

    const handleNext = () => {
        setActiveStep((prev) => (prev + 1) % heroArticles.length);
    };

    const handleBack = () => {
        setActiveStep((prev) => (prev - 1 + heroArticles.length) % heroArticles.length);
    };

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    if (heroArticles.length === 0 && gridArticles.length === 0) return null;

    return (
        <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', pb: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Box
                    component="img"
                    src={blackLogo}
                    alt="Black Logo"
                    sx={{
                        width: { xs: 120, sm: 140, md: 170, lg: 190 },
                        height: "auto",
                        objectFit: "contain",
                    }}
                />
            </Box>

            <Grid container spacing={3}>
                {/* Hero Slider Section */}
                {heroArticles.length > 0 && (
                    <Grid size={{ xs: 12 }}>
                        <Box sx={{ position: 'relative', '&:hover .slider-arrow': { opacity: 1 } }}>
                            <Fade in={true} timeout={500} key={activeStep}>
                                <Box>
                                    <ArticleCard
                                        article={heroArticles[activeStep]}
                                        aspectRatio="2 / 1"
                                        titleVariant="h4"
                                        tagColor="primary"
                                    />
                                </Box>
                            </Fade>

                            {/* Arrows & Dots */}
                            {heroArticles.length > 1 && (
                                <>
                                    <IconButton
                                        className="slider-arrow"
                                        onClick={handleBack}
                                        sx={{ position: 'absolute', top: '50%', left: 16, transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.2)', color: 'white', backdropFilter: 'blur(4px)', opacity: { xs: 1, md: 0 }, transition: 'opacity 0.3s', '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' }, zIndex: 2 }}
                                    >
                                        <ArrowBackIosNew />
                                    </IconButton>
                                    <IconButton
                                        className="slider-arrow"
                                        onClick={handleNext}
                                        sx={{ position: 'absolute', top: '50%', right: 16, transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.2)', color: 'white', backdropFilter: 'blur(4px)', opacity: { xs: 1, md: 0 }, transition: 'opacity 0.3s', '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' }, zIndex: 2 }}
                                    >
                                        <ArrowForwardIos />
                                    </IconButton>
                                    <Box sx={{ position: 'absolute', bottom: 20, right: 20, display: 'flex', gap: 1, zIndex: 2 }}>
                                        {heroArticles.map((_, index) => (
                                            <Circle key={index} sx={{ fontSize: 10, color: index === activeStep ? 'white' : 'rgba(255,255,255,0.4)', cursor: 'pointer' }} onClick={() => setActiveStep(index)} />
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Grid>
                )}

                {/* Grid Articles */}
                {gridArticles.map((article, index) => (
                    <Grid size={{ xs: 12, md: 4 }} key={article.articleId}>
                        <ArticleCard
                            article={article}
                            aspectRatio="3 / 4"
                            titleVariant="h6"
                            tagColor={index % 2 === 0 ? 'primary' : 'warning'}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default CommercialArticlesSection;